/**
 * useDoodlePattern — Selects the active doodle pattern.
 *
 * Rotation logic:
 *  1. Context-aware overrides (Indian tax season, festivals, etc.)
 *  2. Falls back to ISO week-based rotation through standard patterns
 *  3. Respects user preference to disable doodles
 *
 * @since 1.1.0
 */
import { useEffect, useState } from 'react';
import type { DoodlePattern } from './patterns';

const DOODLE_PREF_KEY = 'karr:doodles-enabled';

/**
 * Get ISO week number (1-53) for a given date.
 */
function getISOWeek(date: Date): number {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

/**
 * Check for context-aware overrides based on Indian business calendar.
 * Returns a pattern ID or null.
 */
function getContextOverride(date: Date): string | null {
    const month = date.getMonth(); // 0-indexed
    const day = date.getDate();

    // March 15-31: ITR / Tax season (India FY ends March 31)
    if (month === 2 && day >= 15) return 'tax';

    // July 1-31: GST return season
    if (month === 6) return 'tax';

    // October 15 - November 15: Diwali / Festive season
    if ((month === 9 && day >= 15) || (month === 10 && day <= 15)) return 'festive';

    // December 20-31: Year-end celebrations
    if (month === 11 && day >= 20) return 'festive';

    // January 1-5: New Year
    if (month === 0 && day <= 5) return 'festive';

    return null;
}

/**
 * Read user preference for doodles. Defaults to enabled.
 */
export function isDoodlesEnabled(): boolean {
    if (typeof window === 'undefined') return true;
    const pref = localStorage.getItem(DOODLE_PREF_KEY);
    return pref !== 'false';
}

/**
 * Toggle doodles on/off in localStorage.
 */
export function setDoodlesEnabled(enabled: boolean): void {
    localStorage.setItem(DOODLE_PREF_KEY, String(enabled));
}

/** The pattern for a date: a seasonal override, else the week's turn in the rotation. */
export function pickPattern(patterns: readonly DoodlePattern[], map: Readonly<Record<string, DoodlePattern>>, now: Date): DoodlePattern | null {
    const override = getContextOverride(now);
    if (override && map[override]) return map[override];
    // Standard rotation pool (excludes seasonal-only patterns)
    const pool = patterns.filter(p => !['festive', 'tax'].includes(p.id));
    return pool.length ? pool[getISOWeek(now) % pool.length] : null;
}

/**
 * Hook: Get the current doodle pattern based on date & preference.
 *
 * bundletrim (4 Oct 2026): the patterns (12 KB of SVG) load in their own
 * chunk after the first paint instead of riding in the app's first load, which
 * had gone over its 350 KB budget. The doodles sit at about 3% opacity, so
 * arriving a moment later is invisible.
 *
 * @returns The active pattern (or null if disabled, or not loaded yet)
 */
export function useDoodlePattern(): DoodlePattern | null {
    const [pattern, setPattern] = useState<DoodlePattern | null>(null);
    useEffect(() => {
        if (!isDoodlesEnabled()) return;
        let live = true;
        import('./patterns')
            .then((m) => { if (live) setPattern(pickPattern(m.DOODLE_PATTERNS, m.PATTERN_MAP, new Date())); })
            .catch(() => { /* decoration only: no doodles is fine */ });
        return () => { live = false; };
    }, []);
    return pattern;
}
